// @flow
import React from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import Modal from '@localytics/loc-components/lib/Modal';
import { StandardNode, STANDARD_MESSAGE_WIDTH } from './StandardNode';
import PushNodeModal from './PushNodeModal';
import withLeaveDialog from './withLeaveDialog';
import TruncatingText from './TruncatingText';
import { deselectNode } from '../actions/tree';
import { getOrchestrationVersionCreatives, modifyNode } from '../actions/api';
import { validateCreatives } from '../util/ValidationUtils';
import { MESSAGE_ICON, MESSAGE_FILL_COLOR } from '../constants/Style';
import {
  CREATIVE_RICH_MSG_URL_HTTPS,
  CREATIVE_RICH_MSG_URL_FILETYPE
} from '../constants/ValidationTypes';
import { getQueryParams } from '../util/RouterUtils';
import { ORCH_ENDPOINT_PATH } from '../config';

type CreativeData = {
  title: string,
  body: string,
  attachment_url: string,
  attachment_type: string,
  deeplink: string,
  keyValues: Object
};

export type PushNodeProps = {
  message_type: string,
  creative_ids: Array<number>,
  display_name: ?string,
  height: number,
  rx: number,
  ry: number,
  selected: boolean,
  dispatch: Function,
  creatives: Array<{
    id: number,
    creativeType: string,
    app_uuid: string,
    data: CreativeData
  }>,
  apps: {
    uuid: string,
    name: string,
    integration_platform: string,
    icon: { url: string }
  },
  appsInVersion: Array<{
    app_uuid: string,
    olap_id: number
  }>,
  nodesInVersion: Object,
  currentVersionId: number,
  currentOrchestrationId: number,
  currentOrgId: number,
  appGroupId: number,
  editable: boolean,
  name: string
};

type PushNodeState = {
  modalClass: string
};

export const DEFAULT_MESSAGE_DISPLAY_NAME = 'Untitled Message';

class PushNode extends React.Component<PushNodeProps, PushNodeState> {
  constructor(props: PushNodeProps) {
    super(props);
    this.state = { modalClass: 'PushNodeModal' };
  }

  submitForm: Function = ({ creatives, display_name }) => {
    if (!this.props.editable) {
      return;
    }
    const creativeErrors = validateCreatives(creatives);
    if (creativeErrors[CREATIVE_RICH_MSG_URL_HTTPS]) {
      alert(creativeErrors[CREATIVE_RICH_MSG_URL_HTTPS]);
      return;
    } else if (creativeErrors[CREATIVE_RICH_MSG_URL_FILETYPE]) {
      alert(creativeErrors[CREATIVE_RICH_MSG_URL_FILETYPE]);
      return;
    }

    const {
      currentVersionId: versionId,
      currentOrchestrationId: orchestrationId,
      currentOrgId: orgId,
      appGroupId,
      creative_ids,
      appsInVersion,
      nodesInVersion,
      name: nodeName
    } = this.props;
    const promises = creatives.map(creative => {
      const endpoint = `${ORCH_ENDPOINT_PATH}v1/orchestration/organization/${
        orgId
      }/orchestration/${orchestrationId}/version/${versionId}/creatives/${
        creative.id
      }`;
      const options = {
        credentials: 'include',
        method: 'PUT',
        headers: {
          Accepts: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
          'content-type': 'application/json'
        },
        body: JSON.stringify({
          data: {
            ...creative.data
          }
        })
      };
      return fetch(endpoint, options);
    });
    Promise.all(promises)
      .then(() => {
        this.props.dispatch(
          getOrchestrationVersionCreatives(orgId, orchestrationId, versionId)
        );
      })
      .catch(err => {
        console.log(err);
      });
    this.props.dispatch(
      modifyNode({
        apps: appsInVersion,
        nodes: nodesInVersion,
        versionId,
        orchestrationId,
        orgId,
        appGroupId,
        nodeName,
        newNodeData: {
          display_name,
          node_type: 'message',
          creative_ids
        }
      })
    );
    this.props.dispatch(deselectNode());
  };
  cancelForm: Function = () => this.props.dispatch(deselectNode());
  closeForm: Function = () => this.props.dispatch(deselectNode());

  render() {
    const {
      message_type: messageType,
      display_name: displayName,
      selected,
      height,
      rx,
      ry,
      editable
    } = this.props;
    const { creatives = [], apps: appMap, creative_ids } = this.props;
    const appsAndCreativesLoaded =
      creatives.length && Object.keys(appMap).length;
    const filteredCreatives = appsAndCreativesLoaded
      ? creatives
          .filter(creative => creative_ids.includes(creative.id))
          .slice()
          .sort((a, b) => {
            const { name: nameA = '' } = appMap[a.app_uuid];
            const { name: nameB = '' } = appMap[b.app_uuid];
            return nameA > nameB ? 1 : -1;
          })
      : [];
    const appsForCreatives = filteredCreatives
      ? filteredCreatives.map(creative => {
          const app = appMap[creative.app_uuid] || {};
          const {
            uuid = '',
            name = '',
            integration_platform = '', // eslint-disable-line
            icon: { url = '' } = {}
          } = app;

          return {
            appId: uuid,
            name,
            platform: integration_platform,
            iconURL: url
          };
        })
      : '';

    const ModalWithLeaveDialog = editable ? withLeaveDialog(Modal) : Modal;

    return (
      <StandardNode
        height={height}
        rx={rx}
        ry={ry}
        icon={MESSAGE_ICON}
        iconFill={MESSAGE_FILL_COLOR}
      >
        <ModalWithLeaveDialog
          show={selected}
          className={this.state.modalClass}
          onHide={() => {}}
        >
          <PushNodeModal
            creatives={filteredCreatives}
            apps={appsForCreatives}
            height={height}
            rx={rx}
            ry={ry}
            submitFormCallback={this.submitForm}
            cancelCallback={this.cancelForm}
            closeCallback={this.closeForm}
            editable={editable}
            displayName={displayName}
          />
        </ModalWithLeaveDialog>
        <TruncatingText
          content={displayName || DEFAULT_MESSAGE_DISPLAY_NAME}
          maxWidth={STANDARD_MESSAGE_WIDTH}
          x={0}
          y={-14}
          fill="#555555"
          style={{
            fontSize: '16px'
          }}
        />
        <text
          y="18"
          fill="#818799"
          style={{
            fontSize: '14px',
            textTransform: 'capitalize'
          }}
        >
          {messageType}
        </text>
      </StandardNode>
    );
  }
}

const mapStateToProps = (state, ownProps) => {
  const {
    orgId: currentOrgId,
    orchestrationId: currentOrchestrationId,
    versionId: currentVersionId
  } = getQueryParams(ownProps.location);
  const {
    api: { creatives, versionsByOrchestrationId },
    railsApi: { apps }
  } = state;
  const version =
    versionsByOrchestrationId[currentOrchestrationId][currentVersionId] || {};
  const {
    apps: appsInVersion,
    nodes: nodesInVersion,
    app_group_id: appGroupId
  } = version;

  return {
    creatives,
    apps,
    appsInVersion,
    nodesInVersion,
    appGroupId,
    currentOrgId,
    currentVersionId,
    currentOrchestrationId
  };
};

const ConnectedPushNode = withRouter(connect(mapStateToProps)(PushNode));

export { ConnectedPushNode as PushNode };



// WEBPACK FOOTER //
// ./src/components/PushNode.jsx